import React, { useEffect, useState, useRef } from "react";
import { VersionService, AppVersionResponse } from "../services/VersionService";
import { AppUpdateModal } from "./ui/AppUpdateModal";
import { App } from "@capacitor/app";

const AppVersionManager: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [config, setConfig] = useState<AppVersionResponse | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const checkInProgress = useRef(false);
  const lastCheckTime = useRef(0);

  useEffect(() => {
    let mounted = true;

    const checkVersion = async () => {
      if (checkInProgress.current) return;

      const now = Date.now();
      const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

      // Throttle frequent checks (e.g., on app resume)
      if (now - lastCheckTime.current < CHECK_INTERVAL) {
        console.log("Version check throttled");
        return;
      }

      checkInProgress.current = true;
      lastCheckTime.current = now;
      if (mounted) setIsChecking(true);

      // Check immediately to prevent race conditions and run parallel to Splash Screen
      try {
        const data = await VersionService.checkAppVersion();

        if (mounted && data) {
          // Show modal if Force Update OR Update Available
          if (data.forceUpdate || data.updateAvailable) {
            console.log(
              `Version Check: Update needed (Force: ${data.forceUpdate})`,
            );
            setConfig(data);
            setModalOpen(true);
          } else {
            // ✅ If app is updated, close the modal!
            console.log("Version Check: App is up to date. Closing modal.");
            setModalOpen(false);
            setConfig(null);
          }
        }
      } catch (error) {
        console.error("Version check failed:", error);
      } finally {
        checkInProgress.current = false;
        if (mounted) setIsChecking(false);
      }
    };
    // Check immediately on mount
    checkVersion();

    // Listen for app resume (foreground) to re-check
    // This is useful if the user went to the store, updated, and came back
    const listener = App.addListener("appStateChange", (state) => {
      if (state.isActive) {
        checkVersion();
      }
    });

    return () => {
      mounted = false;
      listener.then((handle) => handle.remove());
    };
  }, []);

  const handleUpdate = (storeUrl: string) => {
    if (storeUrl) {
      VersionService.openStore(storeUrl);
    }
  };

  const handleDismiss = () => {
    setModalOpen(false);
  };

  if (!config || !modalOpen) return null;

  return (
    <AppUpdateModal
      isOpen={modalOpen}
      forceUpdate={config.forceUpdate}
      message={
        config.forceUpdate
          ? config.forceUpdateMessage
          : config.optionalUpdateMessage ||
          "A new version of the app is available. Updating is recommended for the best experience."
      }
      storeUrl={config.storeUrl}
      onUpdate={handleUpdate}
      onDismiss={handleDismiss}
      isChecking={isChecking}
    />
  );
};

export default AppVersionManager;
